import type { HormoneProfile } from "../profile";
import { EXPOSURE_META } from "../copy";

/**
 * Structure copies manly/report/ResultPage.tsx block for block (client
 * direction 2026-08-20: the funnel pages mirror the main quiz's skeletons):
 * verdict card, meter, two root-cause cards, two VALUE-ONLY stat tiles,
 * optional note, plain CTA. Only the content is this quiz's own: the verdict
 * is his EXPOSURE band and every line stays about the products, never his
 * biology, so nothing on the page claims his symptoms were caused by the
 * exposure. The earlier presentation card, observed prose, refusal paragraph
 * and window tile were cut with that same direction; presentation is still
 * scored (it drives nothing visible here).
 */
export function ResultPage({
  profile, onNext,
}: { profile: HormoneProfile; onNext: () => void }) {
  const exposure = EXPOSURE_META[profile.exposure];

  return (
    <>
      <div className="qk-manly-verdict">
        <div className="qk-manly-kicker">His Exposure Profile</div>
        <div className="qk-manly-verdict-label">{profile.exposure}</div>
        <p>{exposure.line}</p>
      </div>

      <div>
        <div
          className="qk-manly-meter-track"
          role="img"
          aria-label={`${profile.exposure}: ${exposure.line}`}
        >
          <div className="qk-manly-meter-mark" style={{ left: exposure.pos }} aria-hidden="true" />
        </div>
        <div className="qk-manly-meter-ends"><span>Early</span><span>Saturated</span></div>
      </div>

      <div className="qk-manly-causes">
        <div className="qk-manly-cause">
          <div className="qk-manly-tag">Primary root cause</div>
          <h3>{profile.primary.name}</h3>
          <p>{profile.primary.copy}</p>
        </div>
        <div className="qk-manly-cause">
          <div className="qk-manly-tag">Secondary root cause</div>
          <h3>{profile.secondary.name}</h3>
          <p>{profile.secondary.copy}</p>
        </div>
      </div>

      <div className="qk-manly-stats">
        <div className="qk-manly-cause">
          <div className="qk-manly-tag">Fragrance</div>
          <h3>{profile.fragrance.state}</h3>
        </div>
        <div className="qk-manly-cause">
          <div className="qk-manly-tag">Plan focus</div>
          <h3>{profile.planFocus}</h3>
        </div>
      </div>

      {profile.sawDoctor && (
        <p className="qk-manly-note">
          You had this looked at and were told nothing was alarming. That is a statement about the
          range he falls inside, not about where in that range he was headed.
        </p>
      )}

      {/* Pinned (client request): unlike the sibling's result page this one
          must keep its CTA reachable however tall the phone. */}
      <button type="button" className="qk-cta qk-manly-cta-sticky" onClick={onNext}>
        See his projection
      </button>
    </>
  );
}
